import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { Card } from '@sorare/core/src/components/card/Card';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import {
  ANY_SPORT_MARKET,
  ANY_SPORT_PLAYERS_SLUG,
  ANY_SPORT_SERIES_MARKET,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { css } from '@sorare/css';

import { PlayerAvatar } from '#src/components/player/PlayerAvatar/index.tsx';

import type {
  TaskCardsMyRelatedCardsQuery,
  TaskCardsMyRelatedCardsQueryVariables,
  TaskCards_taskInterface,
} from './__generated__/index.graphql';

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--unit);
  width: 100%;
`;

const CardSlot = styled(ButtonBase)`
  aspect-ratio: var(--card-aspect-ratio);
  min-width: 0;
`;

const RemainingSlot = styled(CardSlot)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--c-nd-150);
  border: 1px dashed var(--c-nd-400);
  border-radius: var(--unit);
`;

const missingPlayerAvatar = css`
  --size: calc(8 * var(--unit));
`;

const TASK_CARDS_MY_RELATED_CARDS_QUERY = gql`
  query TaskCardsMyRelatedCardsQuery(
    $taskId: ID!
    $after: String
    $pageSize: Int
  ) {
    currentUser {
      slug
      task(id: $taskId, bypassRebuildCollectionTask: true) {
        id
        ... on CardsCountTargetTask {
          id
          myRelatedCards(after: $after, first: $pageSize) {
            nodes {
              slug
              anyPlayer {
                slug
              }
              ...Card_anyCard
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    }
  }
  ${Card.fragments.anyCard}
` as TypedDocumentNode<
  TaskCardsMyRelatedCardsQuery,
  TaskCardsMyRelatedCardsQueryVariables
>;

type Props = {
  task: TaskCards_taskInterface;
};

const PAGE_SIZE = 10;

export const TaskCards = ({ task }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const openDialog = useOpenDialog();
  const isCardsCountTask = isType(task, 'CardsCountTargetTask');
  const players = (isCardsCountTask && task.players) || [];
  const pageSize = Math.max(PAGE_SIZE, players.length);
  const { data, loading, loadMore } = usePaginatedQuery(
    TASK_CARDS_MY_RELATED_CARDS_QUERY,
    {
      variables: { taskId: idFromObject(task.id), pageSize },
      connection: 'AnyCardInterfaceConnection',
      skip: !isCardsCountTask,
    }
  );

  if (!isCardsCountTask) {
    return null;
  }

  const paginatedTask = data?.currentUser?.task;
  const myRelatedCards = isType(paginatedTask, 'CardsCountTargetTask')
    ? paginatedTask.myRelatedCards
    : null;

  const { target } = task;
  const marketPath = generateSportContextPath(
    task.rarity === Rarity.common ? ANY_SPORT_SERIES_MARKET : ANY_SPORT_MARKET
  );
  const cardList = myRelatedCards?.nodes || [];
  const visibleCards = cardList;
  const collectedPlayerSlugs = new Set(
    cardList.map(({ anyPlayer }) => anyPlayer.slug)
  );
  const hasPlayers = players.length > 0;
  const missingPlayers = players
    .filter(({ slug }) => !collectedPlayerSlugs.has(slug))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const hasMoreRelatedCards = myRelatedCards?.pageInfo?.hasNextPage;
  const unknownRemainingSlotsCount = Math.min(
    Math.max(
      0,
      myRelatedCards?.pageInfo.hasNextPage ? 0 : target - cardList.length
    ),
    pageSize
  );

  return (
    <Vertical gap={2}>
      <CardsGrid>
        {visibleCards.map(card => (
          <CardSlot
            key={card.slug}
            onClick={() => openDialog(DialogKey.card, card.slug)}
          >
            <Card card={card} width={160} />
          </CardSlot>
        ))}
        {missingPlayers.map(missingPlayer => (
          <RemainingSlot
            key={missingPlayer.slug}
            to={generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
              params: { slug: missingPlayer.slug },
            })}
          >
            <PlayerAvatar
              player={missingPlayer}
              className={missingPlayerAvatar}
            />
          </RemainingSlot>
        ))}
        {!hasPlayers &&
          Array.from({ length: unknownRemainingSlotsCount }, (_, i) => (
            <RemainingSlot key={`remaining-${i}`} to={marketPath}>
              <BrandM bold color="var(--c-nd-500)">
                ?
              </BrandM>
            </RemainingSlot>
          ))}
      </CardsGrid>
      {!!hasMoreRelatedCards && (
        <Button
          size="medium"
          color="quaternary"
          disabled={loading}
          onClick={async () => {
            await loadMore(false, {
              after: myRelatedCards?.pageInfo?.endCursor,
            });
          }}
        >
          <FormattedMessage {...glossary.viewMore} />
        </Button>
      )}
    </Vertical>
  );
};

TaskCards.fragments = {
  taskInterface: gql`
    fragment TaskCards_taskInterface on TaskInterface {
      id
      rarity
      ... on CardsCountTargetTask {
        id
        target
        players {
          slug
          displayName
          ...GameplayPlayerAvatar_anyPlayer
        }
        cardSet {
          slug
          branding {
            id
            colors {
              surfacePrimary
            }
          }
        }
      }
    }
    ${PlayerAvatar.fragments.anyPlayer}
  ` as TypedDocumentNode<TaskCards_taskInterface>,
};
